'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { useSearchParams } from 'next/navigation';
import { useHistoryListener } from '@/hooks/useHistoryListener';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { parseJson } from '@/lib/common';
import { parsePathnameToSearchType } from '@/lib/publicSearchPaths';
import { PUBLIC_SEARCH_STATE_PARAM } from '@/lib/saleCataloguePaths';
import {
  FilterGroup,
  FilterInput,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import { cancelPendingAiSearch } from './aiSearchRequest';
import {
  deserializePublicSearchFilterState,
  deserializePublicSearchPage,
  deserializePublicSearchString,
  PublicSearchUrlLogicalOperator,
  serializePublicSearchFilter,
  serializePublicSearchFilterGroup,
  serializePublicSearchSort,
  type SerializedPublicSearchState,
} from '@/lib/publicSearchUrlCodec';
import {
  normalizeFilter,
  normalizePublicSearchFilterGroup,
} from '@/lib/normalizePublicSearchUrlState';

const SEARCH_STATE_PARAM = PUBLIC_SEARCH_STATE_PARAM;

// One-shot flag, set by committed searches (AI / "Show results" / "View
// results"): the next URL write pushes a new history entry instead of replacing,
// so Back steps through committed searches. Continuous edits keep replaceState.
// Module-level so commit handlers in other components can arm it without props.
let pendingHistoryPush = false;

/** Arm the next public-search URL write to push a new history entry. */
export function armSearchHistoryPush() {
  pendingHistoryPush = true;
}

// Nginx and Apache both default to ~8 192 bytes for the request line. Stay
// well below that so page-refresh and shared links survive default server
// configs even when other query params are present.
const MAX_SEARCH_URL_LENGTH = 7500;

type PublicSearchUrlState = {
  searchTerm: string;
  aiSearchPrompt: string;
  isAiMode: boolean;
  filters: FilterInput[];
  filterGroups: FilterGroup[];
  commonFilterOperator: string;
  groupsOperator: string;
  gridSortModel: GridSortModel;
  myAnimals: boolean;
  animalsOnSaleOnly: boolean;
  /** 0-based result page (BLS-396). */
  page: number;
};

function normalizeUrlState(state: PublicSearchUrlState) {
  const filters = state.filters
    .map(normalizeFilter)
    .filter((filter): filter is FilterInput => filter !== null);
  const filterGroups = state.filterGroups
    .map(normalizePublicSearchFilterGroup)
    .filter((group): group is FilterGroup => group !== null);

  return {
    searchTerm: state.searchTerm.trim(),
    aiSearchPrompt: state.aiSearchPrompt.trim(),
    isAiMode: state.isAiMode,
    filters,
    filterGroups,
    commonFilterOperator:
      state.commonFilterOperator === LogicalOperator.and
        ? LogicalOperator.and
        : LogicalOperator.or,
    groupsOperator:
      state.groupsOperator === LogicalOperator.and
        ? LogicalOperator.and
        : LogicalOperator.or,
    gridSortModel: (state.gridSortModel ?? [])
      .filter((sort) => sort.field)
      .map((sort) => ({
        field: sort.field,
        sort: sort.sort ?? null,
      })),
    myAnimals: state.myAnimals,
    animalsOnSaleOnly: state.animalsOnSaleOnly,
    page: deserializePublicSearchPage(state.page),
  };
}

type NormalizedUrlState = ReturnType<typeof normalizeUrlState>;

function serializeUrlState(
  state: NormalizedUrlState,
): SerializedPublicSearchState {
  const out: SerializedPublicSearchState = {};

  if (state.searchTerm) out.q = state.searchTerm;

  if (state.aiSearchPrompt) {
    out.ai = state.aiSearchPrompt;
  } else if (state.isAiMode) {
    out.aiMode = true;
  }

  if (state.filters.length > 0) {
    out.filters = state.filters.map(serializePublicSearchFilter);
    if (state.commonFilterOperator === LogicalOperator.or) {
      out.op = PublicSearchUrlLogicalOperator.or;
    }
  }

  if (state.filterGroups.length > 0) {
    out.groups = state.filterGroups.map(serializePublicSearchFilterGroup);
    if (state.groupsOperator === LogicalOperator.or) {
      out.gop = PublicSearchUrlLogicalOperator.or;
    }
  }

  if (state.gridSortModel.length > 0) {
    const sortItems = serializePublicSearchSort(state.gridSortModel);
    if (sortItems.length > 0) out.sort = sortItems;
  }

  if (state.myAnimals) out.my = true;
  if (state.animalsOnSaleOnly) out.onSale = true;
  // BLS-396: keep the page the user is on in the URL so returning from an
  // animal / member / lot detail (and Back/Forward, refresh, shared links)
  // lands back on it instead of page 1.
  if (state.page > 0) out.page = state.page;

  return out;
}

function deserializeUrlState(
  parsed: SerializedPublicSearchState,
): PublicSearchUrlState {
  const isAiMode = !!(parsed.ai || parsed.aiMode);

  return {
    searchTerm: deserializePublicSearchString(parsed.q),
    aiSearchPrompt: deserializePublicSearchString(parsed.ai),
    isAiMode,
    ...deserializePublicSearchFilterState(parsed),
    myAnimals: !!parsed.my,
    animalsOnSaleOnly: !!parsed.onSale,
    page: deserializePublicSearchPage(parsed.page),
  };
}

function hasActiveUrlState(state: NormalizedUrlState) {
  return !!(
    state.searchTerm ||
    state.aiSearchPrompt ||
    state.isAiMode ||
    state.filters.length > 0 ||
    state.filterGroups.length > 0 ||
    state.gridSortModel.length > 0 ||
    state.myAnimals ||
    state.animalsOnSaleOnly ||
    state.page > 0
  );
}

/**
 * Bidirectionally syncs the public-search store (filters, sort, AI mode, etc.)
 * with a `?search=<json>` URL parameter via `history.replaceState`.
 *
 * Effect 1 reads the URL and applies it to the store (on mount and on browser
 * back/forward navigation). Effect 2 writes store changes back to the URL.
 * A ref holds the latest serialized state so Effect 1 doesn't overwrite a
 * change that Effect 2 hasn't flushed to the URL yet.
 *
 * Mount this hook once, inside `SearchHeader`, which is already rendered for
 * every public-search page.
 */
export function usePublicSearchUrlSync() {
  const searchType = useSearchType();
  const searchParams = useSearchParams();
  const {
    basicSearchInput,
    aiSearchPrompt,
    isAiMode,
    setAiSearchPrompt,
    setOpenResult,
    setSearchDisplayTerm,
    setIsAiMode,
  } = useSearchStore();
  const {
    filters,
    filterGroups,
    commonFilterOperator,
    groupsOperator,
    setFilters,
    setFilterGroups,
    clearFilterGroups,
    updateCommonFilterOperator,
    setGroupsOperator,
  } = useFilterStore(searchType);
  const {
    gridSortModel,
    myAnimals,
    animalsOnSaleOnly,
    paginationModel,
    setGridSortModel,
    setMyAnimals,
    setAnimalsOnSaleOnly,
    setPaginationModel,
  } = useGridStore();

  // BLS-396: page moves in and out of the URL alongside the query state. The
  // page size stays a session preference (it isn't serialized), so writes read
  // the current size from the store rather than carrying a stale one.
  const applyPage = useCallback(
    (page: number) => {
      const { pageSize, page: currentPage } =
        useGridStore.getState().paginationModel;
      if (currentPage === page) return;
      setPaginationModel({ page, pageSize });
    },
    [setPaginationModel],
  );

  const currentUrlState = useMemo(
    () =>
      normalizeUrlState({
        searchTerm: basicSearchInput ?? '',
        aiSearchPrompt: isAiMode ? (aiSearchPrompt ?? '') : '',
        isAiMode,
        filters,
        filterGroups,
        commonFilterOperator,
        groupsOperator,
        gridSortModel,
        myAnimals,
        animalsOnSaleOnly,
        page: paginationModel.page,
      }),
    [
      aiSearchPrompt,
      basicSearchInput,
      commonFilterOperator,
      filterGroups,
      filters,
      groupsOperator,
      gridSortModel,
      isAiMode,
      myAnimals,
      animalsOnSaleOnly,
      paginationModel.page,
    ],
  );

  const currentUrlStateSerialized = useMemo(
    () => JSON.stringify(currentUrlState),
    [currentUrlState],
  );

  // Use a ref so applyStateFromLocation can read the latest serialized state
  // without being recreated every time the store changes. Recreating the
  // callback would trigger Effect 1 before Effect 2 has written the new URL,
  // causing the stale URL to be re-applied and reverting the user's change.
  const currentUrlStateSerializedRef = useRef<string>(
    currentUrlStateSerialized,
  );
  useEffect(() => {
    currentUrlStateSerializedRef.current = currentUrlStateSerialized;
  }, [currentUrlStateSerialized]);
  const skipNextUrlWriteRef = useRef(false);
  const skipNextHistoryEventRef = useRef(false);
  const lastInternalRawRef = useRef<string | null | undefined>(undefined);

  const applyStateFromLocation = useCallback(
    (event?: Event) => {
      if (
        skipNextHistoryEventRef.current &&
        (event?.type === 'pushState' || event?.type === 'replaceState')
      ) {
        skipNextHistoryEventRef.current = false;
        return;
      }

      if (parsePathnameToSearchType(location.pathname) !== searchType) return;

      const raw = new URLSearchParams(location.search).get(SEARCH_STATE_PARAM);
      // Next.js also observes native history writes and updates useSearchParams.
      // Ignore that second notification for URLs this hook just wrote itself.
      if (event == null && raw === lastInternalRawRef.current) return;
      lastInternalRawRef.current = undefined;

      if (!raw) {
        const emptyState = normalizeUrlState({
          searchTerm: '',
          aiSearchPrompt: '',
          isAiMode: false,
          filters: [],
          filterGroups: [],
          commonFilterOperator: LogicalOperator.or,
          groupsOperator: LogicalOperator.or,
          gridSortModel: [],
          myAnimals: false,
          animalsOnSaleOnly: false,
          // BLS-396: the page is deliberately excluded from "is the URL empty?".
          // Next re-writes the URL from its own router state after a Back
          // traversal and drops a param written with history.replaceState, so a
          // missing `?search=` is not proof the user left the page behind.
          // Filter/sort state still resets below; the page follows from that
          // reset via PublicSearchInput, and Effect 2 puts the param back.
          page: useGridStore.getState().paginationModel.page,
        });

        if (
          JSON.stringify(emptyState) === currentUrlStateSerializedRef.current
        ) {
          return;
        }

        cancelPendingAiSearch();
        skipNextUrlWriteRef.current = true;
        setSearchDisplayTerm('');
        setAiSearchPrompt('');
        setIsAiMode(false);
        setFilters([]);
        clearFilterGroups();
        updateCommonFilterOperator(LogicalOperator.or);
        setGroupsOperator(LogicalOperator.or);
        setGridSortModel([]);
        setMyAnimals(false);
        setAnimalsOnSaleOnly(false);
        // Animal result visibility is derived from the restored criteria.
        setOpenResult(true);
        return;
      }

      const parsed = parseJson(raw, null) as SerializedPublicSearchState | null;
      if (!parsed || typeof parsed !== 'object') return;

      const normalized = normalizeUrlState(deserializeUrlState(parsed));

      if (JSON.stringify(normalized) === currentUrlStateSerializedRef.current) {
        return;
      }

      cancelPendingAiSearch();
      skipNextUrlWriteRef.current = true;
      setSearchDisplayTerm(
        normalized.isAiMode
          ? normalized.aiSearchPrompt || normalized.searchTerm
          : normalized.searchTerm,
      );
      setAiSearchPrompt(normalized.isAiMode ? normalized.aiSearchPrompt : '');
      setIsAiMode(normalized.isAiMode);
      setFilters(normalized.filters);
      if (normalized.filterGroups.length > 0) {
        setFilterGroups(normalized.filterGroups);
      } else {
        clearFilterGroups();
      }
      updateCommonFilterOperator(normalized.commonFilterOperator);
      setGroupsOperator(normalized.groupsOperator);
      setGridSortModel(normalized.gridSortModel);
      setMyAnimals(normalized.myAnimals);
      setAnimalsOnSaleOnly(normalized.animalsOnSaleOnly);
      applyPage(normalized.page);
      setOpenResult(true);
    },
    [
      applyPage,
      clearFilterGroups,
      searchType,
      setAiSearchPrompt,
      setFilterGroups,
      setFilters,
      setGridSortModel,
      setIsAiMode,
      setMyAnimals,
      setAnimalsOnSaleOnly,
      setOpenResult,
      setGroupsOperator,
      setSearchDisplayTerm,
      updateCommonFilterOperator,
    ],
  );

  useEffect(() => {
    applyStateFromLocation();
  }, [applyStateFromLocation, searchParams]);

  useHistoryListener({
    callback: applyStateFromLocation,
  });

  useEffect(() => {
    if (parsePathnameToSearchType(location.pathname) !== searchType) return;

    // A programmatic URL→store sync (mount, Back/Forward, or a committed search
    // clearing state) supersedes any pending commit: disarm so the push can't
    // leak onto an unrelated later write.
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      pendingHistoryPush = false;
      return;
    }

    const serialized = serializeUrlState(currentUrlState);
    const raw = hasActiveUrlState(currentUrlState)
      ? JSON.stringify(serialized)
      : undefined;
    const currentRaw = new URLSearchParams(location.search).get(
      SEARCH_STATE_PARAM,
    );

    // No URL change: do NOT consume the pending push here. A committed AI search
    // clears its filters first (an intermediate write that is frequently a no-op
    // relative to the URL) and only writes the real query a tick later, once the
    // results arrive. Consuming the flag on this no-op would leave that real
    // write to replaceState, so Back would skip past the search to an older one.
    if ((raw ?? null) === currentRaw) return;

    const params = new URLSearchParams(location.search);
    if (raw) {
      params.set(SEARCH_STATE_PARAM, raw);
    } else {
      params.delete(SEARCH_STATE_PARAM);
    }

    const qs = params.toString();
    const candidate = `${location.origin}${location.pathname}${qs ? `?${qs}` : ''}`;
    // Couldn't write the committed search; disarm so the push doesn't leak onto
    // a later continuous edit and turn a keystroke into its own Back entry.
    if (candidate.length > MAX_SEARCH_URL_LENGTH) {
      pendingHistoryPush = false;
      return;
    }

    // This write is the one the committed search armed: consume the flag now,
    // when an actual history entry is being written.
    const armed = pendingHistoryPush;
    pendingHistoryPush = false;

    const nextUrl = `${location.pathname}${qs ? `?${qs}` : ''}`;
    lastInternalRawRef.current = raw ?? null;
    skipNextHistoryEventRef.current = true;
    // An armed write opens its own history entry
    if (armed) {
      history.pushState({}, '', nextUrl);
      window.dispatchEvent(new Event('pushState'));
    } else {
      history.replaceState({}, '', nextUrl);
      window.dispatchEvent(new Event('replaceState'));
    }
    // BLS-396: `searchParams` is a dependency so this re-runs when the URL
    // changes underneath us. Next rebuilds the URL from its own router state
    // after a Back traversal and drops the param this hook wrote natively; the
    // no-op guard above means the only writes that happen are the ones that put
    // it back.
  }, [currentUrlState, searchParams, searchType]);
}
